import React, { useState, useEffect } from 'react';
import axios from '../config/axios';
import { FiImage, FiX, FiTarget, FiMap, FiList, FiGift, FiZap, FiFileText, FiInfo, FiLink, FiCheck } from 'react-icons/fi';
import GalleryImageSelector from '../components/GalleryImageSelector';
import GalleryImagePicker from '../components/GalleryImagePicker';
import { useTranslation } from '../hooks/useTranslation';

const LandingPageEditor = () => {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [message, setMessage] = useState(null);
  const [showGallerySelector, setShowGallerySelector] = useState(false);
  const [showCtaGallerySelector, setShowCtaGallerySelector] = useState(false);
  const [imagePicker, setImagePicker] = useState({ isOpen: false, section: null, arrayName: null, index: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get('/landing-page/content');
      setContent(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching landing page content:', error);
      setMessage({ type: 'error', text: 'Failed to load content' });
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(
        `/landing-page/content/${section}`,
        { content: content[section] }
      );
      setMessage({ type: 'success', text: `${section} section updated successfully!` });
    } catch (error) {
      console.error('Error saving content:', error);
      setMessage({ type: 'error', text: 'Failed to save changes' });
    }
    setSaving(false);
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateArrayField = (section, arrayName, index, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayName]: prev[section][arrayName].map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addArrayItem = (section, arrayName, template) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayName]: [...prev[section][arrayName], template]
      }
    }));
  };

  const removeArrayItem = (section, arrayName, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayName]: prev[section][arrayName].filter((_, i) => i !== index)
      }
    }));
  };

  const handleGalleryImageSelect = (imageData) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        backgroundImage: imageData.url,
        backgroundImageId: imageData.id,
        backgroundImageCrop: imageData.crop
      }
    }));
    setShowGallerySelector(false);
  };

  const clearHeroBackground = () => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        backgroundImage: '',
        backgroundImageId: null,
        backgroundImageCrop: null
      }
    }));
  };

  const handleCtaGalleryImageSelect = (imageData) => {
    setContent(prev => ({
      ...prev,
      cta: {
        ...prev.cta,
        backgroundImage: imageData.url,
        backgroundImageId: imageData.id,
        backgroundImageCrop: imageData.crop
      }
    }));
    setShowCtaGallerySelector(false);
  };

  const clearCtaBackground = () => {
    setContent(prev => ({
      ...prev,
      cta: {
        ...prev.cta,
        backgroundImage: '',
        backgroundImageId: null,
        backgroundImageCrop: null
      }
    }));
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Video file must be less than 50MB' });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('video', file);

      const response = await axios.post('/landing-page/upload-hero-video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setContent(prev => ({
        ...prev,
        hero: {
          ...prev.hero,
          backgroundVideo: response.data.videoUrl,
          backgroundVideoFilename: response.data.filename,
          backgroundImage: '', // Clear image when video is set
          backgroundImageId: null,
          backgroundImageCrop: null
        }
      }));

      setMessage({ type: 'success', text: 'Video uploaded successfully! Click "Save Hero Section" below to apply changes.' });
    } catch (error) {
      console.error('Error uploading video:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to upload video' });
    } finally {
      setUploading(false);
    }
  };

  const clearHeroVideo = async () => {
    if (content.hero.backgroundVideoFilename) {
      try {
        await axios.delete(`/landing-page/hero-video/${content.hero.backgroundVideoFilename}`);
      } catch (error) {
        console.error('Error deleting video:', error);
      }
    }

    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        backgroundVideo: '',
        backgroundVideoFilename: '',
        posterImage: '',
        posterImageFilename: ''
      }
    }));
  };

  const handlePosterUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Poster image must be less than 10MB' });
      return;
    }

    setUploading(true);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('poster', file);

      const response = await axios.post('/landing-page/upload-hero-poster', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setContent(prev => ({
        ...prev,
        hero: {
          ...prev.hero,
          posterImage: response.data.posterUrl,
          posterImageFilename: response.data.filename
        }
      }));

      setMessage({ type: 'success', text: 'Poster image uploaded successfully! Click "Save Hero Section" below to apply changes.' });
    } catch (error) {
      console.error('Error uploading poster:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to upload poster image' });
    } finally {
      setUploading(false);
    }
  };

  const clearHeroPoster = async () => {
    if (content.hero.posterImageFilename) {
      try {
        await axios.delete(`/landing-page/hero-poster/${content.hero.posterImageFilename}`);
      } catch (error) {
        console.error('Error deleting poster:', error);
      }
    }

    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        posterImage: '',
        posterImageFilename: ''
      }
    }));
  };

  const openImagePicker = (section, arrayName, index) => {
    setImagePicker({ isOpen: true, section, arrayName, index });
  };

  const handleImagePickerSelect = (imageUrl, cropData = null) => {
    const { section, arrayName, index } = imagePicker;
    if (section && arrayName !== null && index !== null) {
      updateArrayField(section, arrayName, index, 'image', imageUrl);
      if (cropData) {
        updateArrayField(section, arrayName, index, 'imageCrop', cropData);
      } else {
        updateArrayField(section, arrayName, index, 'imageCrop', null);
      }
    }
    setImagePicker({ isOpen: false, section: null, arrayName: null, index: null });
  };

  const getCurrentImageUrl = () => {
    const { section, arrayName, index } = imagePicker;
    if (!content || !section || arrayName === null || index === null) return '';
    return content[section]?.[arrayName]?.[index]?.image || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!content) {
    return <div className="text-center text-red-600">Failed to load content</div>;
  }

  const sections = [
    { id: 'hero', name: 'Hero Section', Icon: FiTarget },
    { id: 'discover', name: 'Discover Section', Icon: FiMap },
    { id: 'how_it_works', name: 'How It Works', Icon: FiList },
    { id: 'rewards', name: 'Rewards', Icon: FiGift },
    { id: 'cta', name: 'Call to Action', Icon: FiZap },
    { id: 'footer', name: 'Footer', Icon: FiFileText }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-600">{t('cms.nav.landingPage')}</h1>
          <p className="text-gray-600">Customize your public website content</p>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Section Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-6">
            <h2 className="font-semibold text-gray-900 mb-3">Sections</h2>
            <div className="space-y-1">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <section.Icon className="mr-2 w-5 h-5" />
                  {section.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Editor */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Hero Section */}
            {activeSection === 'hero' && content.hero && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4"><FiTarget className="inline-block mr-2" />Hero Section</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={content.hero.title}
                      onChange={(e) => updateField('hero', 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                    <input
                      type="text"
                      value={content.hero.tagline}
                      onChange={(e) => updateField('hero', 'tagline', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={content.hero.description}
                      onChange={(e) => updateField('hero', 'description', e.target.value)}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Background Media</label>

                    {/* Video Preview */}
                    {content.hero.backgroundVideo ? (
                      <div className="space-y-3">
                        <div className="relative rounded-lg overflow-hidden border-2 border-gray-200">
                          <video
                            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${content.hero.backgroundVideo}`}
                            className="w-full h-48 object-cover"
                            controls
                            muted
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-xs font-medium">
                            Video
                          </div>
                          <button
                            type="button"
                            onClick={clearHeroVideo}
                            className="absolute top-2 left-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Poster Image for Video */}
                        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-blue-900 mb-1">Video Poster Image</h4>
                              <p className="text-xs text-blue-700 mb-3">Upload an image to show before the video loads</p>

                              {content.hero.posterImage ? (
                                <div className="space-y-2">
                                  <div className="relative rounded-lg overflow-hidden border-2 border-blue-300">
                                    <img
                                      src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${content.hero.posterImage}`}
                                      alt="Video poster preview"
                                      className="w-full h-32 object-cover"
                                    />
                                    <button
                                      type="button"
                                      onClick={clearHeroPoster}
                                      className="absolute top-2 left-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                    >
                                      <FiX className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <p className="text-xs text-blue-600 flex items-center gap-1"><FiCheck className="w-3 h-3" /> Poster image set</p>
                                </div>
                              ) : (
                                <label className="btn btn-primary flex items-center justify-center gap-2 cursor-pointer text-sm">
                                  <FiImage className="w-4 h-4" />
                                  <span>Upload Poster Image</span>
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={handlePosterUpload}
                                    className="hidden"
                                    disabled={uploading}
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : content.hero.backgroundImage ? (
                      /* Image Preview */
                      <div className="space-y-3">
                        <div className="relative rounded-lg overflow-hidden border-2 border-gray-200">
                          <img
                            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${content.hero.backgroundImage}`}
                            alt="Hero background preview"
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-xs font-medium">
                            {content.hero.backgroundImageCrop ? 'Cropped Image' : 'Image'}
                          </div>
                          <button
                            type="button"
                            onClick={clearHeroBackground}
                            className="absolute top-2 left-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>

                        {content.hero.backgroundImageCrop && (
                          <div className="text-xs text-gray-600 bg-primary-50 border border-primary-200 rounded-lg p-2">
                            <strong>Crop Applied:</strong> {Math.round(content.hero.backgroundImageCrop.width)}% × {Math.round(content.hero.backgroundImageCrop.height)}%
                            {' '}at position ({Math.round(content.hero.backgroundImageCrop.x)}%, {Math.round(content.hero.backgroundImageCrop.y)}%)
                          </div>
                        )}
                      </div>
                    ) : (
                      /* No media selected */
                      <div>
                        <p className="text-xs text-gray-500 mb-3">
                          Leave empty to use the default autumn gradient background.
                        </p>
                      </div>
                    )}

                    {/* Upload Options */}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <button
                        type="button"
                        onClick={() => setShowGallerySelector(true)}
                        className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
                      >
                        <FiImage className="w-8 h-8 text-gray-400 group-hover:text-primary-500 transition-colors" />
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700 group-hover:text-primary-700">Select Image</p>
                          <p className="text-xs text-gray-500 mt-1">From gallery</p>
                        </div>
                      </button>

                      <label className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors cursor-pointer group">
                        <svg className="w-8 h-8 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-700 group-hover:text-primary-700">
                            {uploading ? 'Uploading...' : 'Upload Video'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">MP4, WebM (max 50MB)</p>
                        </div>
                        <input
                          type="file"
                          accept="video/mp4,video/webm,video/ogg"
                          onChange={handleVideoUpload}
                          disabled={uploading}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Primary Button (White)
                      </label>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Button Text (leave empty to hide)</label>
                          <input
                            type="text"
                            value={content.hero.primaryCta?.text || ''}
                            onChange={(e) => updateField('hero', 'primaryCta', { ...content.hero.primaryCta, text: e.target.value })}
                            placeholder="e.g., Start Your Adventure"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Link URL</label>
                          <input
                            type="text"
                            value={content.hero.primaryCta?.link || ''}
                            onChange={(e) => updateField('hero', 'primaryCta', { ...content.hero.primaryCta, link: e.target.value })}
                            placeholder="#download"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Secondary Button (Transparent)
                      </label>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Button Text (leave empty to hide)</label>
                          <input
                            type="text"
                            value={content.hero.secondaryCta?.text || ''}
                            onChange={(e) => updateField('hero', 'secondaryCta', { ...content.hero.secondaryCta, text: e.target.value })}
                            placeholder="e.g., Learn More"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Link URL</label>
                          <input
                            type="text"
                            value={content.hero.secondaryCta?.link || ''}
                            onChange={(e) => updateField('hero', 'secondaryCta', { ...content.hero.secondaryCta, link: e.target.value })}
                            placeholder="#how-it-works"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong><FiInfo className="inline-block mr-1" /> Tip:</strong> Leave the button text empty to hide that button. Both buttons can be hidden independently.
                    </p>
                  </div>
                  <button
                    onClick={() => handleSave('hero')}
                    disabled={saving}
                    className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Hero Section'}
                  </button>
                </div>
              </div>
            )}

            {/* Discover Section */}
            {activeSection === 'discover' && content.discover && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4"><FiMap className="inline-block mr-2" />Discover Section</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={content.discover.title}
                      onChange={(e) => updateField('discover', 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={content.discover.subtitle}
                      onChange={(e) => updateField('discover', 'subtitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Features</label>
                      <button
                        onClick={() => addArrayItem('discover', 'features', { icon: '✨', title: 'New Feature', description: '' })}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        + Add Feature
                      </button>
                    </div>
                    <div className="space-y-3">
                      {content.discover.features.map((feature, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-2xl">{feature.icon}</span>
                            <button
                              onClick={() => removeArrayItem('discover', 'features', index)}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                          <input
                            type="text"
                            value={feature.icon}
                            onChange={(e) => updateArrayField('discover', 'features', index, 'icon', e.target.value)}
                            placeholder="Icon (emoji)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-primary-500"
                          />
                          <div className="mb-2">
                            <label className="block text-xs text-gray-600 mb-1">Feature Image (optional)</label>
                            {feature.image ? (
                              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                <img
                                  src={feature.image.startsWith('http') ? feature.image : `${process.env.REACT_APP_API_URL.replace('/api', '')}${feature.image}`}
                                  alt="Preview"
                                  className="w-full h-24 object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openImagePicker('discover', 'features', index)}
                                    className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    Change
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateArrayField('discover', 'features', index, 'image', '')}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openImagePicker('discover', 'features', index)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
                              >
                                <FiImage className="w-5 h-5 text-gray-400 group-hover:text-primary-500" />
                                <span className="text-sm text-gray-600 group-hover:text-primary-700">Select from Gallery</span>
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={feature.title}
                            onChange={(e) => updateArrayField('discover', 'features', index, 'title', e.target.value)}
                            placeholder="Title"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-primary-500"
                          />
                          <textarea
                            value={feature.description}
                            onChange={(e) => updateArrayField('discover', 'features', index, 'description', e.target.value)}
                            placeholder="Description"
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSave('discover')}
                    disabled={saving}
                    className="btn btn-primary w-full disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Discover Section'}
                  </button>
                </div>
              </div>
            )}

            {/* How It Works Section */}
            {activeSection === 'how_it_works' && content.how_it_works && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4"><FiList className="inline-block mr-2" />How It Works</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={content.how_it_works.title}
                      onChange={(e) => updateField('how_it_works', 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={content.how_it_works.subtitle}
                      onChange={(e) => updateField('how_it_works', 'subtitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Steps</label>
                    <div className="space-y-3">
                      {content.how_it_works.steps.map((step, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold">
                              {step.number}
                            </span>
                            <input
                              type="text"
                              value={step.title}
                              onChange={(e) => updateArrayField('how_it_works', 'steps', index, 'title', e.target.value)}
                              placeholder="Step title"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                          </div>
                          <div className="mb-2">
                            <label className="block text-xs text-gray-600 mb-1">Step Image (optional)</label>
                            {step.image ? (
                              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                <img
                                  src={step.image.startsWith('http') ? step.image : `${process.env.REACT_APP_API_URL.replace('/api', '')}${step.image}`}
                                  alt="Preview"
                                  className="w-full h-24 object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openImagePicker('how_it_works', 'steps', index)}
                                    className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    Change
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateArrayField('how_it_works', 'steps', index, 'image', '')}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openImagePicker('how_it_works', 'steps', index)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
                              >
                                <FiImage className="w-5 h-5 text-gray-400 group-hover:text-primary-500" />
                                <span className="text-sm text-gray-600 group-hover:text-primary-700">Select from Gallery</span>
                              </button>
                            )}
                          </div>
                          <textarea
                            value={step.description}
                            onChange={(e) => updateArrayField('how_it_works', 'steps', index, 'description', e.target.value)}
                            placeholder="Step description"
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSave('how_it_works')}
                    disabled={saving}
                    className="btn btn-primary w-full disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save How It Works Section'}
                  </button>
                </div>
              </div>
            )}

            {/* Rewards Section */}
            {activeSection === 'rewards' && content.rewards && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4"><FiGift className="inline-block mr-2" />Rewards</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={content.rewards.title}
                      onChange={(e) => updateField('rewards', 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input
                      type="text"
                      value={content.rewards.subtitle}
                      onChange={(e) => updateField('rewards', 'subtitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Reward Items</label>
                      <button
                        onClick={() => addArrayItem('rewards', 'items', { icon: '<FiGift />', title: 'New Reward', description: '' })}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        + Add Reward
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {content.rewards.items.map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-2xl">{item.icon}</span>
                            <button
                              onClick={() => removeArrayItem('rewards', 'items', index)}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              ×
                            </button>
                          </div>
                          <input
                            type="text"
                            value={item.icon}
                            onChange={(e) => updateArrayField('rewards', 'items', index, 'icon', e.target.value)}
                            placeholder="Icon"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-primary-500"
                          />
                          <div className="mb-2">
                            <label className="block text-xs text-gray-600 mb-1">Reward Image (optional)</label>
                            {item.image ? (
                              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                                <img
                                  src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_API_URL.replace('/api', '')}${item.image}`}
                                  alt="Preview"
                                  className="w-full h-24 object-cover"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-40 transition-all flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => openImagePicker('rewards', 'items', index)}
                                    className="px-3 py-1.5 bg-white text-gray-700 rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    Change
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateArrayField('rewards', 'items', index, 'image', '')}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openImagePicker('rewards', 'items', index)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
                              >
                                <FiImage className="w-5 h-5 text-gray-400 group-hover:text-primary-500" />
                                <span className="text-sm text-gray-600 group-hover:text-primary-700">Select from Gallery</span>
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => updateArrayField('rewards', 'items', index, 'title', e.target.value)}
                            placeholder="Title"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 focus:ring-2 focus:ring-primary-500"
                          />
                          <textarea
                            value={item.description}
                            onChange={(e) => updateArrayField('rewards', 'items', index, 'description', e.target.value)}
                            placeholder="Description"
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleSave('rewards')}
                    disabled={saving}
                    className="btn btn-primary w-full disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Rewards Section'}
                  </button>
                </div>
              </div>
            )}

            {/* CTA Section */}
            {activeSection === 'cta' && content.cta && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4"><FiZap className="inline-block mr-2" />Call to Action</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={content.cta.title}
                      onChange={(e) => updateField('cta', 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={content.cta.description}
                      onChange={(e) => updateField('cta', 'description', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Background Image (optional)</label>

                    {content.cta.backgroundImage ? (
                      <div className="space-y-3">
                        {/* Preview */}
                        <div className="relative rounded-lg overflow-hidden border-2 border-gray-200">
                          <img
                            src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${content.cta.backgroundImage}`}
                            alt="CTA background preview"
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-xs font-medium">
                            {content.cta.backgroundImageCrop ? 'Cropped' : 'Full Image'}
                          </div>
                          <button
                            type="button"
                            onClick={clearCtaBackground}
                            className="absolute top-2 left-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setShowCtaGallerySelector(true)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <FiImage className="w-4 h-4" />
                            Change Image
                          </button>
                        </div>

                        {content.cta.backgroundImageCrop && (
                          <div className="text-xs text-gray-600 bg-primary-50 border border-primary-200 rounded-lg p-2">
                            <strong>Crop Applied:</strong> {Math.round(content.cta.backgroundImageCrop.width)}% × {Math.round(content.cta.backgroundImageCrop.height)}%
                            {' '}at position ({Math.round(content.cta.backgroundImageCrop.x)}%, {Math.round(content.cta.backgroundImageCrop.y)}%)
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <button
                          type="button"
                          onClick={() => setShowCtaGallerySelector(true)}
                          className="w-full flex flex-col items-center justify-center gap-3 px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors group"
                        >
                          <FiImage className="w-12 h-12 text-gray-400 group-hover:text-primary-500 transition-colors" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-700 group-hover:text-primary-700">Select from Gallery</p>
                            <p className="text-xs text-gray-500 mt-1">Choose and crop an image for your CTA section</p>
                          </div>
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          Leave empty to use the default autumn gradient background.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Primary Button (White)
                      </label>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Button Text (leave empty to hide)</label>
                          <input
                            type="text"
                            value={content.cta.primaryCta?.text || ''}
                            onChange={(e) => updateField('cta', 'primaryCta', { ...content.cta.primaryCta, text: e.target.value })}
                            placeholder="e.g., Download App (Android)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Link URL</label>
                          <input
                            type="text"
                            value={content.cta.primaryCta?.link || ''}
                            onChange={(e) => updateField('cta', 'primaryCta', { ...content.cta.primaryCta, link: e.target.value })}
                            placeholder="/gamimap-v1.3.apk"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Secondary Button (Transparent)
                      </label>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Button Text (leave empty to hide)</label>
                          <input
                            type="text"
                            value={content.cta.secondaryCta?.text || ''}
                            onChange={(e) => updateField('cta', 'secondaryCta', { ...content.cta.secondaryCta, text: e.target.value })}
                            placeholder="e.g., Learn More"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Link URL</label>
                          <input
                            type="text"
                            value={content.cta.secondaryCta?.link || ''}
                            onChange={(e) => updateField('cta', 'secondaryCta', { ...content.cta.secondaryCta, link: e.target.value })}
                            placeholder="#how-it-works"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <strong><FiInfo className="inline-block mr-1" /> Tip:</strong> Leave the button text empty to hide that button. Both buttons can be hidden independently.
                    </p>
                  </div>
                  <button
                    onClick={() => handleSave('cta')}
                    disabled={saving}
                    className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save CTA Section'}
                  </button>
                </div>
              </div>
            )}

            {/* Footer Section */}
            {activeSection === 'footer' && content.footer && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4"><FiFileText className="inline-block mr-2" />Footer</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">About Text</label>
                    <input
                      type="text"
                      value={content.footer.about}
                      onChange={(e) => updateField('footer', 'about', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={content.footer.description}
                      onChange={(e) => updateField('footer', 'description', e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={content.footer.email}
                      onChange={(e) => updateField('footer', 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Powered By Text</label>
                    <input
                      type="text"
                      value={content.footer.poweredBy}
                      onChange={(e) => updateField('footer', 'poweredBy', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <button
                    onClick={() => handleSave('footer')}
                    disabled={saving}
                    className="btn btn-primary w-full disabled:opacity-50 transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Footer Section'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Preview Link */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-gray-900">Preview Your Changes</h3>
                <p className="text-sm text-gray-600 mt-1">View the public landing page</p>
              </div>
              <a
                href="https://test.exploreandwin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <FiLink className="inline-block mr-2" />
                Open Landing Page
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Image Selector Modal - Hero */}
      <GalleryImageSelector
        isOpen={showGallerySelector}
        onClose={() => setShowGallerySelector(false)}
        onSelect={handleGalleryImageSelect}
        currentImage={content.hero.backgroundImage}
      />

      {/* Gallery Image Selector Modal - CTA */}
      <GalleryImageSelector
        isOpen={showCtaGallerySelector}
        onClose={() => setShowCtaGallerySelector(false)}
        onSelect={handleCtaGalleryImageSelect}
        currentImage={content.cta.backgroundImage}
      />

      {/* Gallery Image Picker Modal */}
      <GalleryImagePicker
        isOpen={imagePicker.isOpen}
        onClose={() => setImagePicker({ isOpen: false, section: null, arrayName: null, index: null })}
        onSelect={handleImagePickerSelect}
        selectedImageUrl={getCurrentImageUrl()}
      />
    </div>
  );
};

export default LandingPageEditor;
